import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Gamepad2, Search, Link2, Unlink, Loader2, RefreshCw, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { eaApi } from '../lib/api';

const StatBox = ({ label, value, color = 'text-white' }) => (
  <div className="bg-[#050508] border border-white/10 rounded p-3 text-center">
    <p className={`font-mono font-bold text-xl ${color}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</p>
  </div>
);

export const EAStatsSection = ({ club, canManage, isItalian }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await eaApi.clubStats(club.club_id);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore caricamento statistiche EA' : 'EA stats load error'));
    } finally {
      setLoading(false);
    }
  }, [club.club_id, isItalian]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = async () => {
    if (searchName.trim().length < 2) return;
    setSearching(true);
    setResults(null);
    try {
      const res = await eaApi.search(searchName.trim());
      setResults(res.data.results);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Ricerca EA fallita' : 'EA search failed'));
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async (r) => {
    setLinking(true);
    try {
      await eaApi.linkClub(club.club_id, { ea_club_id: r.ea_club_id, ea_club_name: r.name, platform: 'common-gen5' });
      toast.success(isItalian ? `Collegato a "${r.name}" su EA Pro Clubs!` : `Linked to "${r.name}" on EA Pro Clubs!`);
      setResults(null);
      setSearchName('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Collegamento fallito' : 'Link failed'));
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm(isItalian ? 'Rimuovere il collegamento al club EA?' : 'Remove the EA club link?')) return;
    try {
      await eaApi.unlinkClub(club.club_id);
      toast.success(isItalian ? 'Collegamento EA rimosso' : 'EA link removed');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const overall = data?.overall;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="bg-[#0a0d15] border border-white/10"
      data-testid="ea-stats-section"
    >
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
        <Gamepad2 size={18} className="text-[#00F0FF]" />
        <h3 className="font-heading uppercase tracking-widest text-sm font-bold">
          {isItalian ? 'Statistiche EA Pro Clubs' : 'EA Pro Clubs Stats'}
        </h3>
        {data?.linked && (
          <span className="px-2 py-0.5 text-[10px] rounded bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 uppercase tracking-wider">
            {data.ea_link?.ea_club_name}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {data?.linked && (
            <Button size="sm" variant="ghost" className="h-7 text-slate-400 hover:text-white" onClick={load} data-testid="ea-refresh-btn">
              <RefreshCw size={13} />
            </Button>
          )}
          {data?.linked && canManage && (
            <Button size="sm" variant="ghost" className="h-7 text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10" onClick={handleUnlink} data-testid="ea-unlink-btn">
              <Unlink size={13} />
            </Button>
          )}
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" />
            {isItalian ? 'Caricamento da EA…' : 'Loading from EA…'}
          </div>
        ) : !data?.linked ? (
          canManage ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">
                {isItalian
                  ? 'Collega il tuo club EA Sports FC Pro Clubs: cerca il nome del club su EA e collegalo per vedere le statistiche ufficiali.'
                  : 'Link your EA Sports FC Pro Clubs team: search its EA name and link it to show official stats.'}
              </p>
              <div className="flex gap-2">
                <Input
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={isItalian ? 'Nome del club su EA…' : 'EA club name…'}
                  className="bg-[#050508] border-white/10"
                  data-testid="ea-search-input"
                />
                <Button onClick={handleSearch} disabled={searching || searchName.trim().length < 2} className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black" data-testid="ea-search-btn">
                  {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                </Button>
              </div>
              {results && (
                results.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="ea-no-results">{isItalian ? 'Nessun club trovato su EA' : 'No EA clubs found'}</p>
                ) : (
                  <div className="divide-y divide-white/5 border border-white/10 rounded overflow-hidden">
                    {results.map((r) => (
                      <div key={r.ea_club_id} className="px-3 py-2.5 flex items-center gap-3 bg-[#050508]/60" data-testid={`ea-result-${r.ea_club_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{r.name}</p>
                          <p className="text-[11px] text-slate-500">
                            {r.wins}V {r.draws}P {r.losses}S · Div {r.current_division ?? '–'} · ID {r.ea_club_id}
                          </p>
                        </div>
                        <Button size="sm" disabled={linking} onClick={() => handleLink(r)} className="bg-[#00E676]/15 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/25 h-7 text-xs" data-testid={`ea-link-btn-${r.ea_club_id}`}>
                          <Link2 size={12} className="mr-1" />
                          {isItalian ? 'Collega' : 'Link'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="ea-not-linked">
              {isItalian ? 'Il club non è ancora collegato a EA Pro Clubs.' : 'This club is not linked to EA Pro Clubs yet.'}
            </p>
          )
        ) : (
          <div className="space-y-5">
            {overall && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="ea-overall-stats">
                <StatBox label={isItalian ? 'Partite' : 'Games'} value={overall.games_played} />
                <StatBox label={isItalian ? 'Vittorie' : 'Wins'} value={overall.wins} color="text-[#00E676]" />
                <StatBox label={isItalian ? 'Pareggi' : 'Draws'} value={overall.draws} color="text-[#FFEA00]" />
                <StatBox label={isItalian ? 'Sconfitte' : 'Losses'} value={overall.losses} color="text-[#FF1744]" />
                <StatBox label={isItalian ? 'Divisione' : 'Division'} value={overall.best_division ?? '–'} color="text-[#00F0FF]" />
                <StatBox label="Skill Rating" value={overall.skill_rating} color="text-[#FF9100]" />
              </div>
            )}
            {data.members?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1.5">
                  <Trophy size={12} className="text-[#FFD700]" />
                  {isItalian ? 'Giocatori EA' : 'EA Players'}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ea-members-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/10">
                        <th className="text-left py-2 pr-2">{isItalian ? 'Giocatore' : 'Player'}</th>
                        <th className="text-center py-2 px-2">{isItalian ? 'Partite' : 'Games'}</th>
                        <th className="text-center py-2 px-2">{isItalian ? 'Gol' : 'Goals'}</th>
                        <th className="text-center py-2 px-2">Assist</th>
                        <th className="text-center py-2 px-2">{isItalian ? 'Media Voto' : 'Avg Rating'}</th>
                        <th className="text-center py-2 pl-2">Win %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {data.members.map((m) => (
                        <tr key={m.name} className="hover:bg-white/5">
                          <td className="py-2 pr-2 font-medium text-white">{m.name}</td>
                          <td className="py-2 px-2 text-center font-mono">{m.games_played}</td>
                          <td className="py-2 px-2 text-center font-mono text-[#00E676]">{m.goals}</td>
                          <td className="py-2 px-2 text-center font-mono text-[#D500F9]">{m.assists}</td>
                          <td className="py-2 px-2 text-center font-mono text-[#FFEA00]">{m.rating_avg?.toFixed(1)}</td>
                          <td className="py-2 pl-2 text-center font-mono">{m.win_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};
